import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { SEO } from "../components/SEO";
import { toast } from "react-toastify";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { BookOpen } from "lucide-react";
import { API } from "../constants";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts?published_only=true`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading blog posts...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <SEO title="Blog" description="Tips, guides, and insights for better printing from Fit Print Bros." path="/blog" />
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Print Bros Blog</h1>
        <p className="text-xl text-gray-600">Tips, guides, and insights for better printing</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {posts.map((post) => (
          <Link to={`/blog/${post.slug}`} key={post.id}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              {post.featured_image && (
                <div className="aspect-video overflow-hidden rounded-t-lg">
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </div>
              )}
              <CardHeader>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(post.tags || []).map(tag => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <CardTitle className="text-xl hover:text-orange-600 transition-colors">
                  {post.title}
                </CardTitle>
                <CardDescription className="text-gray-600">
                  {post.excerpt}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center text-sm text-gray-500">
                  <BookOpen className="w-4 h-4 mr-2" />
                  <span>By {post.author}</span>
                  <span className="mx-2">•</span>
                  <span>{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No blog posts yet</h3>
          <p className="text-gray-600">Check back soon for printing tips and guides!</p>
        </div>
      )}
    </div>
  );
};

// Blog Post Detail Page

export { Blog };
export default Blog;
